import { useState } from 'react';
import { Button } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataProvider, useNotify, usePermissions, useTranslate } from 'react-admin';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { canRefreshDeviceConfig, parseDeviceConfigGenerationDate } from '@/helpers/device/deviceConfig.js';
import { formatDateToTimeString } from '@/helpers/date/formatDateToTimeString.js';

export const RefreshDeviceConfigButton = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const queryClient = useQueryClient();
    const { permissions } = usePermissions();
    const [isGenerating, setIsGenerating] = useState(false);
    const canRefresh = canRefreshDeviceConfig(permissions);

    const {
        data: lastGeneratedAt,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ['device-config', 'generation', 'latest'],
        queryFn: async () => {
            const result = await dataProvider.getRaw('/device-config/generation/latest');
            const generatedAt = parseDeviceConfigGenerationDate(result?.data);
            return generatedAt ? formatDateToTimeString(generatedAt) : null;
        },
        enabled: canRefresh,
        staleTime: 60_000,
    });

    if (!canRefresh) {
        return null;
    }

    const loading = isGenerating || isFetching;
    const buttonLabel = translate('common.cachedDataLastUpdatedAt', { lastUpdatedAt: lastGeneratedAt });

    const handleClick = async () => {
        setIsGenerating(true);

        try {
            await dataProvider.post('device-config/generate');
            notify('resources.device.notifications.configurationGenerationStarted');
            await refetch();
            await queryClient.invalidateQueries({ queryKey: ['parameter', 'summary'] });
        } catch (error) {
            notify(`${translate('common.errorOccurred')}: ${error.message}`, { type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Button
            color='primary'
            size='small'
            startIcon={<InfoIcon />}
            onClick={handleClick}
            disabled={loading}
            aria-label={buttonLabel}
            sx={{ whiteSpace: 'break-spaces' }}
        >
            {loading ? translate('common.loading') : buttonLabel}
        </Button>
    );
};
